import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import type { DataTablePageEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { DrugEstablishmentItem } from 'Interfaces/ProductWikiInterfaces/drugEstablishments.types';
import './DrugEstablishmentModal.styles.scss';

const DER_PAGE_SIZE = 20;
const DER_SEARCH_DEBOUNCE_MS = 350;

interface DrugEstablishmentModalProps {
  visible: boolean;
  onHide: () => void;
}

const DrugEstablishmentModal: React.FC<DrugEstablishmentModalProps> = ({ visible, onHide }) => {
  const [search, setSearch] = useState('');
  const derSearchFirstOpenRef = useRef(true);
  const tooltipRef = useRef<React.ComponentRef<typeof Tooltip> | null>(null);

  const {
    currentGfc,
    drugEstablishmentsData,
    isDrugEstablishmentsLoading,
    drugEstablishmentsError,
    fetchDrugEstablishments,
  } = productAnalysisStore;

  const establishments = drugEstablishmentsData?.establishments ?? [];
  const derPage = drugEstablishmentsData?.page ?? 1;
  const derPageSize = drugEstablishmentsData?.page_size ?? DER_PAGE_SIZE;
  const derTotalCount = drugEstablishmentsData?.total_count ?? 0;

  const handleDerPageChange = (event: DataTablePageEvent) => {
    const gfc = currentGfc?.trim();
    if (!gfc) return;
    const page = Math.floor(event.first / event.rows) + 1;
    fetchDrugEstablishments({
      gfc,
      page,
      page_size: event.rows,
      search: search.trim() || undefined,
    });
  };

  useEffect(() => {
    if (!visible) return;
    if (derSearchFirstOpenRef.current) {
      derSearchFirstOpenRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const gfc = currentGfc?.trim() ?? '';
      fetchDrugEstablishments({
        gfc,
        page: 1,
        page_size: DER_PAGE_SIZE,
        search: search.trim() || undefined,
      });
    }, DER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, visible, currentGfc, fetchDrugEstablishments]);

  // Re-bind tooltip to Est. Code info icons after table has rendered (they may not exist when Tooltip first mounts or after pagination)
  useEffect(() => {
    if (!visible || establishments.length === 0) return;
    const id = setTimeout(() => {
      tooltipRef.current?.updateTargetEvents('.establishment-code-info');
    }, 0);
    return () => clearTimeout(id);
  }, [visible, establishments.length, derPage]);

  const isExpired = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const expirationTemplate = (rowData: DrugEstablishmentItem) => {
    const expired = isExpired(rowData.expiration_date);
    return (
      <Tag
        value={rowData.expiration_date ?? '—'}
        severity={expired ? 'danger' : 'success'}
        style={{ fontSize: '0.8rem' }}
      />
    );
  };

  const establishmentCodeTemplate = (rowData: DrugEstablishmentItem) => {
    return (
      <div className="establishment-code-cell">
        <span>{rowData.establishment_code}</span>
        <i
          className="pi pi-info-circle establishment-code-info"
          data-pr-tooltip={String(rowData.establishment_code_label ?? rowData.establishment_code ?? '')}
          data-pr-position="right"
          style={{ marginLeft: '0.4rem', color: 'var(--gray-400)', cursor: 'pointer', fontSize: '0.8rem' }}
        />
      </div>
    );
  };

  const feiTemplate = (rowData: DrugEstablishmentItem) => (
    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{rowData.fei_number}</span>
  );

  const handleHide = () => {
    setSearch('');
    derSearchFirstOpenRef.current = true;
    onHide();
  };

  return (
    <Dialog
      header="Drug Establishment Registration"
      visible={visible}
      style={{ width: '1000px' }}
      onHide={handleHide}
      modal
    >
      {/* Single Tooltip for all Est. Code info icons - appendTo body so it is not clipped by DataTable scroll */}
      <Tooltip
        ref={tooltipRef}
        target=".establishment-code-info"
        position="right"
        appendTo={typeof document !== 'undefined' ? document.body : undefined}
        className="drug-establishment-tooltip-overlay"
        baseZIndex={10000}
      />
      <div className="drug-establishment-modal-content">
        <div className="modal-header-info">
          <p className="total-count">
            {isDrugEstablishmentsLoading && !drugEstablishmentsData ? (
              'Loading...'
            ) : (
              <>
                <strong>{derTotalCount}</strong> establishments
              </>
            )}
          </p>
          {!isDrugEstablishmentsLoading && (
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by firm name or FEI number..."
                style={{ width: '300px' }}
              />
            </IconField>
          )}
        </div>

        {isDrugEstablishmentsLoading && !drugEstablishmentsData && (
          <div className="drug-establishment-modal-skeleton">
            <div className="drug-establishment-modal-skeleton__header">
              <Skeleton width="100%" height="1.75rem" borderRadius="4px" />
              <Skeleton width="8rem" height="1.75rem" borderRadius="4px" />
            </div>
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="drug-establishment-modal-skeleton__row">
                <Skeleton width="100%" height="1.5rem" borderRadius="4px" />
                <Skeleton width="7rem" height="1.5rem" borderRadius="4px" />
                <Skeleton width="90%" height="1.5rem" borderRadius="4px" />
                <Skeleton width="6rem" height="1.5rem" borderRadius="4px" />
                <Skeleton width="4rem" height="1.5rem" borderRadius="4px" />
              </div>
            ))}
          </div>
        )}

        {drugEstablishmentsError && !drugEstablishmentsData && (
          <div className="labeler-info-message labeler-info-message--error">
            <i className="pi pi-exclamation-triangle labeler-info-message__icon" />
            <p className="labeler-info-message__text">{drugEstablishmentsError}</p>
          </div>
        )}

        {drugEstablishmentsData && (
          <>
            <DataTable
              value={establishments}
              lazy
              stripedRows
              scrollable
              scrollHeight="450px"
              paginator
              totalRecords={derTotalCount}
              first={(derPage - 1) * derPageSize}
              rows={derPageSize}
              onPage={handleDerPageChange}
              loading={isDrugEstablishmentsLoading}
              emptyMessage="No establishments found"
              className="drug-establishment-table"
            >
              <Column
                field="firm_name"
                header="Firm Name"
                style={{ minWidth: '240px', fontWeight: 500 }}
              />
              <Column
                header="FEI Number"
                body={feiTemplate}
                style={{ width: '140px' }}
              />
              <Column
                field="address"
                header="Facility Address"
                style={{ minWidth: '280px', fontSize: '0.875rem' }}
              />
              <Column
                header="Expiration Date"
                body={expirationTemplate}
                style={{ width: '160px' }}
              />
              <Column
                header="Est. Code"
                body={establishmentCodeTemplate}
                style={{ width: '110px' }}
              />
            </DataTable>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default observer(DrugEstablishmentModal);
